import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  NegotiationRoundStatus,
  usePublishNegotiationRoundV2Mutation,
  type NegotiationRoundV2Dto,
  type SourcingEventV2Dto,
} from "../../../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../../../common/documents/DocumentsAsOwnerTable";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../../common/formatting/useDisplayDate";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { RoundInvitesList } from "../RoundInvitesList";

interface Props {
  event: SourcingEventV2Dto;
  round: NegotiationRoundV2Dto;
  previousStep?: () => void;
  onPublished: () => void;
}

export const RoundReviewStep = ({ round, previousStep, onPublished }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const displayDate = useDisplayDate();
  const confirmationModal = useConfirmationModal();
  const [publishRound, { isLoading: isPublishing }] = usePublishNegotiationRoundV2Mutation();

  const isDraft = round.status === NegotiationRoundStatus.Draft;

  const handlePublish = () => {
    confirmationModal.show({
      title: t("Publish negotiation round?"),
      description: t("Invited suppliers will be notified. This cannot be undone."),
      confirmButtonText: t("Publish") ?? "Publish",
      onConfirmed: async () => {
        try {
          await publishRound({ eventId: round.eventId, roundId: round.id }).unwrap();
          toast({ title: t("Negotiation round published"), status: "success" });
          onPublished();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <Box pb="8" pt="10">
      {confirmationModal.modal}
      <Heading as="h2" size="md" mb="6">
        {t("Review and publish")}
      </Heading>

      <Stack spacing="6" maxW="3xl">
        <Stack spacing="1">
          <SummaryRow label={t("Title")} value={round.title || `${t("Negotiation round")} ${round.roundNumber}`} />
          <SummaryRow label={t("Bid deadline")} value={displayDate(round.deadline)} />
          <SummaryRow label={t("Final Round")} value={round.isFinalRound ? t("Yes") : t("No")} />
        </Stack>

        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium">
            {t("Documents")} ({round.documents.length})
          </Text>
          {round.documents.length > 0 ? (
            <DocumentsAsOwnerTable documents={round.documents} />
          ) : (
            <Text fontSize="sm" color="smMuted">
              {t("No documents")}
            </Text>
          )}
        </Stack>

        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium">
            {t("Invited organizations")} ({round.invites.length})
          </Text>
          <RoundInvitesList invites={round.invites} />
        </Stack>

        {isDraft ? (
          <Flex>
            <Button
              colorScheme="teal"
              onClick={handlePublish}
              isLoading={isPublishing}
              isDisabled={round.invites.length === 0}
            >
              {t("Publish round")}
            </Button>
          </Flex>
        ) : (
          <Alert status="info" rounded="lg">
            <AlertIcon />
            <AlertDescription>
              {t("The round is published, and changes are immediately visible to invited suppliers.")}
            </AlertDescription>
          </Alert>
        )}
      </Stack>

      <StepNavigation onPrevious={previousStep} />
    </Box>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Flex gap="3">
    <Text fontSize="sm" color="smMuted" minW="40">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {value}
    </Text>
  </Flex>
);
